/* eslint-disable no-bitwise */
/* eslint-disable no-magic-numbers */
import convertColor from 'color-convert';

import { avg, bound, mod } from '@suntex.core/math';
import { $Color, Color } from '@suntex.core/types';

export type { Color } from '@suntex.core/types';

type HexString = `#${string}`;

export type ColorInput = number | HexString;

const HEX_RADIX = 16;
const HUE_GAMUT = 360;

export const HUES = {
  RED: 0,
  YELLOW: 60, // highlight
  GREEN: 120,
  CYAN: 180,
  BLUE: 240,
  VIOLET: 250, // shadow
  MAGENTA: 300,
};

export function from(c?: undefined): undefined;
export function from(c: ColorInput): Color;
export function from(c?: ColorInput): Color | undefined;
export function from(c?: ColorInput): Color | undefined {
  if (c === undefined) return undefined;
  return $Color.from(c);
}

export function fromHSL(h: number, s: number, l: number): Color {
  return parseInt(convertColor.hsl.hex(h, s, l), HEX_RADIX) as Color;
}

export function toHexString(color: Color): HexString {
  return $Color.toString(color, 'hex');
}

export function toString(color: Color, format: 'hex' | 'hsl'): string {
  const hexString = toHexString(color);

  switch (format) {
    case 'hsl': {
      const [h, s, l] = convertColor.hex.hsl(hexString.substring(1));
      return `hsl(${h}, ${s}%, ${l}%)`;
    }
    default:
      return hexString;
  }
}

export function toRGBA(color: Color, alpha = 1.0): string {
  return `rgba(${(color >> 16) & 0xff}, ${
    (color >> 8) & 0xff
  }, ${color & 0xff}, ${alpha})`;
}

/**
 * Get gradient colors for a given base color
 * @param c Base color
 * @param direction 'highlight' or 'shadow'
 * @param intensity Intensity of the gradient (0-1)
 */
export function getGradientColor(
  color: Color,
  direction: 'highlight' | 'shadow',
  intensity: number = 0.25,
): Color {
  intensity = bound(intensity, 0, 1);

  const model = toFunctionalColorModel(color);

  if (model.family === 'cool') {
    // adjust intensity (lower) for greens and cyans
    intensity = bound(
      intensity *
        avg(
          1,
          // highest at 0 and 1, lowest at 0.5
          Math.pow(
            model.yellowness < 0.5 ? 1 - model.yellowness : model.yellowness,
            2.5, // weight of the adjustment
          ),
        ),
      0,
      1,
    );
  }

  const maxYellownessShiftMagnitude = 0.2;
  const maxLightnessShiftMagnitude = 0.3;
  const maxDesaturationShiftMagnitude = 0.25;
  const dir = direction === 'highlight' ? 1 : -1;
  const targetYellowness = bound(
    model.yellowness + maxYellownessShiftMagnitude * dir,
    0,
    1,
  );
  const yellownessShift = targetYellowness - model.yellowness;
  const lightnessShift =
    (maxYellownessShiftMagnitude - Math.abs(yellownessShift)) *
    maxLightnessShiftMagnitude *
    dir;

  const newColorModel: FunctionalColorModel = {
    family: model.family,
    yellowness: bound(model.yellowness + yellownessShift * intensity, 0, 1),
    lightness: bound(model.lightness + lightnessShift * intensity, 0, 1),
    desaturation: bound(
      model.desaturation - maxDesaturationShiftMagnitude * intensity,
      0,
      1,
    ),
  };

  const newColor = fromFunctionalColorModel(newColorModel);
  const l1 = getLuminance(color);
  const l2 = getLuminance(newColor);

  if (direction === 'highlight') {
    if (l2 > l1 * Math.pow(1.2, intensity)) {
      return newColor;
    } else {
      return fromFunctionalColorModel({
        ...newColorModel,
        lightness: bound(
          model.lightness + maxLightnessShiftMagnitude * intensity,
          0,
          1,
        ),
      });
    }
  } else {
    if (l2 < l1 * Math.pow(0.7, intensity)) {
      return newColor;
    } else {
      return fromFunctionalColorModel({
        ...newColorModel,
        lightness: bound(
          model.lightness - maxLightnessShiftMagnitude * intensity,
          0,
          1,
        ),
      });
    }
  }
}

/**
 * Lighten a color by a given intensity
 * @param color Base color
 * @param intensity How much to lighten the color (0-1, where 1 is pure white)
 * @returns New color lightened
 */
export function lighten(color: Color, intensity: number = 0.5): Color {
  intensity = bound(intensity, 0, 1);

  const model = toFunctionalColorModel(color);

  const newColorModel: FunctionalColorModel = {
    ...model,
    lightness: bound(model.lightness + (1 - model.lightness) * intensity, 0, 1),
    desaturation:
      model.family === 'cool'
        ? bound(
            model.desaturation + (1 - model.desaturation) * intensity * 0.5,
            0,
            1,
          )
        : model.desaturation, // No change to saturation for now
  };

  return fromFunctionalColorModel(newColorModel);
}

/** Functional color model */

const WARM_HUE_RANGE = HUES.YELLOW + HUE_GAMUT - HUES.VIOLET;
const COOL_HUE_RANGE = HUES.VIOLET - HUES.YELLOW;

export function fromFunctionalColorModel(model: FunctionalColorModel): Color {
  const { family, yellowness, lightness, desaturation } = model;

  const h =
    family === 'cool'
      ? HUES.YELLOW + (1 - yellowness) * COOL_HUE_RANGE
      : mod(HUES.VIOLET + yellowness * WARM_HUE_RANGE, HUE_GAMUT);
  const s = (1 - (desaturation ?? 0)) * 100;

  if (lightness !== undefined) {
    return fromHSL(h, s, lightness * 100);
  } else {
    return fromHSL(h, s, 50);
  }
}

export function toFunctionalColorModel(
  color: Color,
): Required<FunctionalColorModel> {
  const hexString = toHexString(color);
  const [h, s, l] = convertColor.hex.hsl(hexString.substring(1));
  const family = h <= HUES.YELLOW || h > HUES.VIOLET ? 'warm' : 'cool';
  const hueBrightness =
    family === 'cool'
      ? 1 - (h - HUES.YELLOW) / COOL_HUE_RANGE
      : mod(h - HUES.VIOLET, HUE_GAMUT) / WARM_HUE_RANGE;
  const desaturation = 1 - s / 100;
  const lightness = l / 100;

  return {
    family,
    yellowness: hueBrightness,
    lightness,
    desaturation,
  };
}

/**
 * Returns the perceived brightness of a color, 0-1
 */
function getLuminance(color: Color) {
  const hexString = toHexString(color);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_x, y, _z] = convertColor.hex.xyz(hexString.substring(1));
  return y / 100;
}

type FunctionalColorModel = {
  family: 'warm' | 'cool';
  yellowness: number; // 0-1, violet-yellow
  lightness?: number; // 0-1, black-white
  desaturation?: number; // 0-1, color-gray
};
